import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import {
  FaTimes, FaStar, FaStarHalfAlt, FaRegStar, FaRoute, FaClock,
  FaExclamationTriangle, FaTachometerAlt, FaMapMarkerAlt, FaWrench,
  FaUserCircle, FaRoad, FaGasPump, FaFlag
} from 'react-icons/fa';

const libraries = ['places', 'marker'];

// --------------- shared helpers (mirrored from Track.js) ---------------

const trackedRadarEvents = [
  { key: 'harsh_braking', label: 'Harsh Braking', color: '#86efac' },
  { key: 'harsh_acceleration', label: 'Harsh Acceleration', color: '#93c5fd' },
  { key: 'overspeeding', label: 'Overspeeding', color: '#fca5a5' },
  { key: 'sustained_speeding', label: 'Sustained Speeding', color: '#c4b5fd' }
];

const getEmptyEventCounts = () => ({
  harsh_braking: 0,
  harsh_acceleration: 0,
  overspeeding: 0,
  sustained_speeding: 0
});

const mapEventNameToRadarKey = (eventNameRaw) => {
  const normalized = String(eventNameRaw || '')
    .trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (!normalized) return null;

  const hasHarsh = normalized.includes('harsh');
  const hasBrake = normalized.includes('brak');
  const hasAccel = normalized.includes('acceler') || normalized.includes('accel');
  const hasSustained = normalized.includes('sustained');
  const hasSpeeding = normalized.includes('speeding') || normalized.includes('overspeed') || normalized.includes('over speed');

  if (hasHarsh && hasBrake) return 'harsh_braking';
  if (hasHarsh && hasAccel) return 'harsh_acceleration';
  if (hasSustained && hasSpeeding) return 'sustained_speeding';
  if (hasSpeeding) return 'overspeeding';

  const exactKey = normalized.replace(/\s+/g, '_');
  if (Object.prototype.hasOwnProperty.call(getEmptyEventCounts(), exactKey)) return exactKey;
  return null;
};

const createRadarDataFromCounts = (counts) =>
  trackedRadarEvents.map((t) => ({ event: t.label, value: counts[t.key] || 0, color: t.color }));

const toTimestampMs = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const createSpeedHistogramData = (speedEvents, timeBatchMinutes = 5) => {
  if (!Array.isArray(speedEvents) || speedEvents.length === 0) return [];
  const valid = speedEvents
    .filter((i) => Number.isFinite(i.speed) && i.speed >= 0 && Number.isFinite(i.timestampMs))
    .sort((a, b) => a.timestampMs - b.timestampMs);
  if (valid.length === 0) return [];

  const batchMs = timeBatchMinutes * 60 * 1000;
  const batches = new Map();
  valid.forEach((i) => {
    const key = Math.floor(i.timestampMs / batchMs);
    if (!batches.has(key)) batches.set(key, []);
    batches.get(key).push(i.speed);
  });

  return Array.from(batches.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([key, speeds]) => ({
      timeLabel: new Date(key * batchMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      avgSpeed: Number((speeds.reduce((s, v) => s + v, 0) / speeds.length).toFixed(1))
    }));
};

const isAnomalyEvent = (eventName) => {
  const n = String(eventName || '').toLowerCase();
  return n.includes('overspeed') || n.includes('over speed') ||
    n.includes('harsh') || n.includes('sustained');
};

const windowOptions = [
  { label: '3h', hours: 3 },
  { label: '6h', hours: 6 },
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 }
];

const FROZEN_GPS_MIN_DURATION_MS = 10 * 60 * 1000;
const FROZEN_GPS_COORD_EPSILON = 0.00001;
const FROZEN_GPS_SPEED_EPSILON = 0.1;
const DRIVING_INTERVAL_MAX_MS = 10 * 60 * 1000;

// --------------- rating helpers ---------------

const computeDriverRating = (eventCounts, totalEvents) => {
  if (totalEvents === 0) return 5;
  const badEvents = (eventCounts.overspeeding || 0) + (eventCounts.sustained_speeding || 0) +
    (eventCounts.harsh_braking || 0) + (eventCounts.harsh_acceleration || 0) +
    (eventCounts.road_limit_violations || 0);
  const ratio = Math.min(1, badEvents / totalEvents);
  return Math.max(1, Math.min(5, Number((5 - ratio * 4).toFixed(1))));
};

const getEmptyRatingCounts = () => ({
  ...getEmptyEventCounts(),
  road_limit_violations: 0
});

const hasRoadLimitViolation = (event) => {
  const speed = parseFloat(event.vehicleSpeed);
  const roadSpeed = parseFloat(event.roadSpeed);
  return Number.isFinite(speed) && Number.isFinite(roadSpeed) && roadSpeed > 0 && speed > roadSpeed;
};

const createAllTimeRatingCounts = (events) => {
  const counts = getEmptyRatingCounts();

  events.forEach((event) => {
    const key = mapEventNameToRadarKey(event.eventName);
    if (key && Object.prototype.hasOwnProperty.call(counts, key)) {
      counts[key] += 1;
    }

    if (hasRoadLimitViolation(event) && key !== 'overspeeding' && key !== 'sustained_speeding') {
      counts.road_limit_violations += 1;
    }
  });

  return counts;
};

const RatingStars = ({ rating }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (rating >= i) stars.push(<FaStar key={i} className="text-yellow-400" />);
    else if (rating >= i - 0.5) stars.push(<FaStarHalfAlt key={i} className="text-yellow-400" />);
    else stars.push(<FaRegStar key={i} className="text-gray-300" />);
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
};

// --------------- distance helper ---------------

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getTelemetry = (event, index) => {
  const lat = parseFloat(event.lat);
  const lon = parseFloat(event.lon);
  const speed = parseFloat(event.vehicleSpeed);
  return {
    index,
    lat,
    lon,
    speed,
    timestampMs: toTimestampMs(event.lastUpdated)
  };
};

const hasValidCoordinates = (telemetry) =>
  Number.isFinite(telemetry.lat) &&
  Number.isFinite(telemetry.lon) &&
  !(telemetry.lat === 0 && telemetry.lon === 0);

const isSameFrozenGpsSignature = (previous, current) =>
  previous.timestampMs > 0 &&
  current.timestampMs > 0 &&
  hasValidCoordinates(previous) &&
  hasValidCoordinates(current) &&
  Number.isFinite(previous.speed) &&
  Number.isFinite(current.speed) &&
  previous.speed > 0 &&
  current.speed > 0 &&
  Math.abs(previous.lat - current.lat) <= FROZEN_GPS_COORD_EPSILON &&
  Math.abs(previous.lon - current.lon) <= FROZEN_GPS_COORD_EPSILON &&
  Math.abs(previous.speed - current.speed) <= FROZEN_GPS_SPEED_EPSILON;

const getFrozenGpsEventIndexes = (events) => {
  const telemetryEvents = events
    .map(getTelemetry)
    .sort((a, b) => a.timestampMs - b.timestampMs);
  const frozenIndexes = new Set();
  let run = [];

  const flushRun = () => {
    if (run.length < 2) return;

    const durationMs = run[run.length - 1].timestampMs - run[0].timestampMs;
    if (durationMs >= FROZEN_GPS_MIN_DURATION_MS) {
      run.forEach((telemetry) => frozenIndexes.add(telemetry.index));
    }
  };

  telemetryEvents.forEach((telemetry) => {
    const previous = run[run.length - 1];
    if (previous && isSameFrozenGpsSignature(previous, telemetry)) {
      run.push(telemetry);
      return;
    }

    flushRun();
    run = [telemetry];
  });
  flushRun();

  return frozenIndexes;
};

const removeFrozenGpsEvents = (events) => {
  const frozenIndexes = getFrozenGpsEventIndexes(events);
  return {
    events: events.filter((_, index) => !frozenIndexes.has(index)),
    filteredCount: frozenIndexes.size
  };
};

const getIgnitionSessions = (events) => {
  const sessions = [];
  let sessionStart = null;
  let hasIgnitionEvents = false;

  events.forEach((event) => {
    const name = String(event.eventName || '').toLowerCase();
    const ts = toTimestampMs(event.lastUpdated);
    if (!ts) return;

    if (name.includes('ignition switched on') || name.includes('ignition on')) {
      sessionStart = ts;
      hasIgnitionEvents = true;
    } else if ((name.includes('ignition switched off') || name.includes('ignition off')) && sessionStart) {
      if (ts > sessionStart) {
        sessions.push({ start: sessionStart, end: ts });
      }
      sessionStart = null;
      hasIgnitionEvents = true;
    }
  });

  if (sessionStart && events.length > 0) {
    const lastTs = toTimestampMs(events[events.length - 1].lastUpdated);
    if (lastTs > sessionStart) {
      sessions.push({ start: sessionStart, end: lastTs });
    }
  }

  return { sessions, hasIgnitionEvents };
};

const isWithinSessions = (timestampMs, sessions) =>
  sessions.some((session) => timestampMs >= session.start && timestampMs <= session.end);

const computeDrivingMsFromTelemetry = (events, sessions = null) => {
  const telemetryEvents = events
    .map(getTelemetry)
    .filter((telemetry) => telemetry.timestampMs > 0 && Number.isFinite(telemetry.speed))
    .sort((a, b) => a.timestampMs - b.timestampMs);

  let drivingMs = 0;
  for (let i = 1; i < telemetryEvents.length; i++) {
    const previous = telemetryEvents[i - 1];
    const current = telemetryEvents[i];
    const intervalMs = current.timestampMs - previous.timestampMs;
    if (intervalMs <= 0 || intervalMs > DRIVING_INTERVAL_MAX_MS) continue;
    if (previous.speed <= 0 || current.speed <= 0) continue;
    if (sessions && (!isWithinSessions(previous.timestampMs, sessions) || !isWithinSessions(current.timestampMs, sessions))) continue;

    drivingMs += intervalMs;
  }

  return drivingMs;
};

// --------------- fuel efficiency estimator ---------------

const computeFuelEfficiency = (points) => {
  if (!points || points.length < 2) return 5;

  let totalPenalty = 0;
  let scoredSegments = 0;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    const prevSpeed = prev.speed ?? 0;
    const currSpeed = curr.speed ?? 0;
    const avgSpeed = (prevSpeed + currSpeed) / 2;

    const prevTs = toTimestampMs(prev.lastUpdated);
    const currTs = toTimestampMs(curr.lastUpdated);
    const timeDeltaSec = (currTs - prevTs) / 1000;

    if (timeDeltaSec <= 0 || timeDeltaSec > 3600) continue;
    scoredSegments++;

    let segPenalty = 0;

    // 1. Excessive speed: quadratic penalty above 80 km/h (max ~1.0 at 160 km/h)
    if (avgSpeed > 80) {
      const excess = avgSpeed - 80;
      segPenalty += Math.min(1, (excess / 80) ** 2);
    }

    // 2. Harsh acceleration / deceleration: penalty for rapid speed changes
    const accel = Math.abs(currSpeed - prevSpeed) / timeDeltaSec; // km/h per second
    if (accel > 3) {
      segPenalty += Math.min(1, (accel - 3) / 12);
    }

    // 3. Idling: both speeds near zero but time is passing
    if (avgSpeed < 3 && timeDeltaSec > 30) {
      const idleMinutes = timeDeltaSec / 60;
      segPenalty += Math.min(1, idleMinutes / 10);
    }

    // 4. Uphill at high speed: positive altitude gain while going fast
    const prevAlt = prev.altitude ?? 0;
    const currAlt = curr.altitude ?? 0;
    const altGain = currAlt - prevAlt;
    if (altGain > 2 && avgSpeed > 60) {
      const gradientFactor = Math.min(1, altGain / 50);
      const speedFactor = Math.min(1, (avgSpeed - 60) / 60);
      segPenalty += gradientFactor * speedFactor * 0.5;
    }

    totalPenalty += Math.min(segPenalty, 2);
  }

  if (scoredSegments === 0) return 5;

  const normalized = totalPenalty / scoredSegments;
  return Number(Math.max(1, Math.min(5, 5 - normalized * 4)).toFixed(1));
};

// --------------- component ---------------

const DriverAnalyticsModal = ({ driver, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [windowHours, setWindowHours] = useState(24);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [speedData, setSpeedData] = useState([]);
  const [radarCounts, setRadarCounts] = useState(() => getEmptyEventCounts());
  const [radarData, setRadarData] = useState(() => createRadarDataFromCounts(getEmptyEventCounts()));
  const [ratingStats, setRatingStats] = useState(() => ({
    counts: getEmptyRatingCounts(),
    totalEvents: 0
  }));
  const [mapPoints, setMapPoints] = useState([]);
  const [totalDrivingMinutes, setTotalDrivingMinutes] = useState(0);
  const [totalDistanceKm, setTotalDistanceKm] = useState(0);
  const [fuelEfficiency, setFuelEfficiency] = useState(5);
  const [tripCount, setTripCount] = useState(0);
  const [filteredGpsCount, setFilteredGpsCount] = useState(0);
  const [showMap, setShowMap] = useState(false);
  const [showAnomalies, setShowAnomalies] = useState(false);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  // ---- fetch events for current window ----
  const fetchEvents = useCallback(async () => {
    if (!driver?.id) return;
    setLoading(true);
    try {
      let windowStart, windowEnd;
      if (isCustomRange && customFrom) {
        windowStart = new Date(customFrom).toISOString();
        windowEnd = customTo ? new Date(customTo + 'T23:59:59').toISOString() : new Date().toISOString();
      } else {
        const now = Date.now();
        windowStart = new Date(now - windowHours * 60 * 60 * 1000).toISOString();
        windowEnd = null;
      }

      let eventsQuery;
      if (windowEnd) {
        eventsQuery = query(
          collection(db, 'events'),
          where('assignedDriver.id', '==', driver.id),
          where('lastUpdated', '>=', windowStart),
          where('lastUpdated', '<=', windowEnd),
          orderBy('lastUpdated', 'asc')
        );
      } else {
        eventsQuery = query(
          collection(db, 'events'),
          where('assignedDriver.id', '==', driver.id),
          where('lastUpdated', '>=', windowStart),
          orderBy('lastUpdated', 'asc')
        );
      }
      const snapshot = await getDocs(eventsQuery);
      const events = [];
      snapshot.forEach((d) => events.push({ id: d.id, ...d.data() }));
      const { events: analyticsEvents, filteredCount } = removeFrozenGpsEvents(events);
      setFilteredGpsCount(filteredCount);

      // speed histogram
      const speedEvents = analyticsEvents
        .map((e) => ({ speed: parseFloat(e.vehicleSpeed), timestampMs: toTimestampMs(e.lastUpdated) }))
        .filter((e) => Number.isFinite(e.speed) && e.speed >= 0 && e.timestampMs > 0);
      setSpeedData(createSpeedHistogramData(speedEvents));

      // radar counts
      const counts = getEmptyEventCounts();
      const overspeedStreak = [];
      const flushStreak = () => {
        if (overspeedStreak.length === 0) return;
        if (overspeedStreak.length >= 2) {
          counts.sustained_speeding += 1;
        } else {
          counts.overspeeding += overspeedStreak.length;
        }
        overspeedStreak.length = 0;
      };

      analyticsEvents.forEach((evt) => {
        const key = mapEventNameToRadarKey(evt.eventName);
        if (key === 'overspeeding') {
          overspeedStreak.push(evt);
          return;
        }
        flushStreak();
        if (key && Object.prototype.hasOwnProperty.call(counts, key)) {
          counts[key] += 1;
        }
      });
      flushStreak();

      setRadarCounts(counts);
      setRadarData(createRadarDataFromCounts(counts));

      // map points — plot every coordinate; flag overspeeding by event name OR speed exceeding road limit
      const points = analyticsEvents
        .map((e) => {
          const lat = parseFloat(e.lat);
          const lon = parseFloat(e.lon);
          if (isNaN(lat) || isNaN(lon) || (lat === 0 && lon === 0)) return null;
          const speed = Number.isFinite(parseFloat(e.vehicleSpeed)) ? Math.round(parseFloat(e.vehicleSpeed)) : null;
          const roadSpeed = Number.isFinite(parseFloat(e.roadSpeed)) ? Math.round(parseFloat(e.roadSpeed)) : null;
          const altitude = Number.isFinite(parseFloat(e.altitude)) ? parseFloat(e.altitude) : null;
          const exceedsRoadSpeed = speed !== null && roadSpeed !== null && roadSpeed > 0 && speed > roadSpeed;
          return {
            id: e.id,
            position: { lat, lng: lon },
            eventName: e.eventName,
            speed,
            roadSpeed,
            altitude,
            lastUpdated: e.lastUpdated,
            isAnomaly: isAnomalyEvent(e.eventName) || exceedsRoadSpeed
          };
        })
        .filter(Boolean);
      setMapPoints(points);

      // total distance from consecutive coordinates
      let distKm = 0;
      for (let i = 1; i < points.length; i++) {
        const previousPoint = points[i - 1];
        const currentPoint = points[i];
        const intervalMs = toTimestampMs(currentPoint.lastUpdated) - toTimestampMs(previousPoint.lastUpdated);
        if (intervalMs <= 0 || intervalMs > DRIVING_INTERVAL_MAX_MS) continue;

        const prev = previousPoint.position;
        const curr = currentPoint.position;
        const seg = haversineKm(prev.lat, prev.lng, curr.lat, curr.lng);
        if (seg < 50) distKm += seg; // ignore jumps > 50 km (GPS glitches)
      }
      setTotalDistanceKm(distKm);
      setFuelEfficiency(computeFuelEfficiency(points));

      const { sessions, hasIgnitionEvents } = getIgnitionSessions(events);
      const drivingMs = computeDrivingMsFromTelemetry(
        analyticsEvents,
        hasIgnitionEvents ? sessions : null
      );

      setTotalDrivingMinutes(Math.round(drivingMs / 60000));
    } catch (err) {
      console.error('Error fetching driver events:', err);
      setFilteredGpsCount(0);
    } finally {
      setLoading(false);
    }
  }, [driver?.id, windowHours, isCustomRange, customFrom, customTo]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ---- fetch all-time events for lifetime driver rating ----
  useEffect(() => {
    const fetchAllTimeRating = async () => {
      if (!driver?.id) return;

      try {
        const ratingQuery = query(
          collection(db, 'events'),
          where('assignedDriver.id', '==', driver.id)
        );
        const snapshot = await getDocs(ratingQuery);
        const events = [];
        snapshot.forEach((eventDoc) => events.push({ id: eventDoc.id, ...eventDoc.data() }));

        const sortedEvents = events.sort((a, b) => toTimestampMs(a.lastUpdated) - toTimestampMs(b.lastUpdated));
        const { events: cleanEvents } = removeFrozenGpsEvents(sortedEvents);
        setRatingStats({
          counts: createAllTimeRatingCounts(cleanEvents),
          totalEvents: cleanEvents.length
        });
      } catch (err) {
        console.error('Error fetching all-time driver rating events:', err);
        setRatingStats({
          counts: getEmptyRatingCounts(),
          totalEvents: 0
        });
      }
    };

    fetchAllTimeRating();
  }, [driver?.id]);

  // ---- fetch trip count ----
  useEffect(() => {
    const fetchTrips = async () => {
      if (!driver?.id) return;
      try {
        const vehiclesSnap = await getDocs(collection(db, 'vehicles'));
        const driverVehicleIds = [];
        vehiclesSnap.forEach((d) => {
          const data = d.data();
          if (data.assignedDriver && data.assignedDriver.id === driver.id) {
            driverVehicleIds.push(d.id);
          }
        });

        if (driverVehicleIds.length === 0) { setTripCount(0); return; }

        let count = 0;
        for (const vId of driverVehicleIds) {
          const routesQuery = query(
            collection(db, 'optimizedRoutes'),
            where('busId', '==', vId),
            where('status', '==', 'active')
          );
          const snap = await getDocs(routesQuery);
          count += snap.size;
        }
        setTripCount(count);
      } catch (err) {
        console.error('Error fetching trip count:', err);
      }
    };
    fetchTrips();
  }, [driver?.id]);

  // ---- fit map bounds when points change ----
  useEffect(() => {
    if (!showMap || !mapRef.current || mapPoints.length === 0 || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    mapPoints.forEach((p) => bounds.extend(p.position));
    mapRef.current.fitBounds(bounds);
    if (mapPoints.length === 1) mapRef.current.setZoom(15);
  }, [showMap, mapPoints]);

  const rating = computeDriverRating(ratingStats.counts, ratingStats.totalEvents);
  const drivingHrs = Math.floor(totalDrivingMinutes / 60);
  const drivingMins = totalDrivingMinutes % 60;
  const drivingTimeLabel = drivingHrs > 0 ? `${drivingHrs}h ${drivingMins}m` : `${drivingMins}m`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              {driver.profilePicture ? (
                <img
                  src={driver.profilePicture}
                  alt={`${driver.firstName} ${driver.lastName}`}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#00E0E0] shadow-sm"
                />
              ) : (
                <FaUserCircle className="w-16 h-16 text-gray-300" />
              )}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {driver.firstName} {driver.lastName}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                    {driver.role === 'driver' ? 'Driver' : 'Bus Assistant'}
                  </span>
                  <div className="flex items-center gap-1">
                    <RatingStars rating={rating} />
                    <span className="text-sm font-semibold text-gray-700 ml-1">{rating}/5</span>
                  </div>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" aria-label="Close">
              <FaTimes />
            </button>
          </div>

          {/* Time window selector */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {windowOptions.map((opt) => (
              <button
                key={opt.hours}
                onClick={() => { setIsCustomRange(false); setWindowHours(opt.hours); }}
                className={`px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${
                  !isCustomRange && windowHours === opt.hours
                    ? 'bg-[#00B8B8] border-[#00B8B8] text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
            <button
              onClick={() => setIsCustomRange(true)}
              className={`px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${
                isCustomRange
                  ? 'bg-[#00B8B8] border-[#00B8B8] text-white'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Custom
            </button>
            {loading && <span className="text-xs text-gray-400 self-center ml-2">Loading...</span>}
            {!loading && filteredGpsCount > 0 && (
              <span className="text-xs text-amber-600 self-center ml-2">
                Filtered {filteredGpsCount} stale GPS reading{filteredGpsCount === 1 ? '' : 's'} (10+ min same speed/location)
              </span>
            )}
          </div>
          {isCustomRange && (
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <label className="text-xs text-gray-600 font-medium">From</label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="px-3 py-1.5 rounded-md border border-gray-300 text-xs focus:ring-2 focus:ring-[#00B8B8] focus:border-[#00B8B8]"
              />
              <label className="text-xs text-gray-600 font-medium">To</label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="px-3 py-1.5 rounded-md border border-gray-300 text-xs focus:ring-2 focus:ring-[#00B8B8] focus:border-[#00B8B8]"
              />
              {!customFrom && <span className="text-xs text-gray-400">Select a start date to load data</span>}
            </div>
          )}

          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard icon={<FaRoad className="text-[#00B8B8]" />} label="Distance Driven" value={`${totalDistanceKm < 1 ? totalDistanceKm.toFixed(2) : totalDistanceKm.toFixed(1)} km`} />
            <StatCard icon={<FaRoute className="text-blue-500" />} label="Trips Assigned" value={tripCount} />
            <StatCard icon={<FaClock className="text-purple-500" />} label="Driving Time" value={drivingTimeLabel} />
            <StatCard icon={<FaGasPump className="text-green-500" />} label="Fuel Efficiency" value={`${fuelEfficiency}/5`} />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Speed over time */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 h-full flex flex-col">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 shrink-0">
                <FaTachometerAlt className="inline mr-2 text-gray-400" />
                Speed Over Time
              </h3>
              <div className="w-full min-h-56 flex-1">
                {speedData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-xs text-gray-500 border border-gray-200 rounded-md">
                    No speed readings in the selected window
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={speedData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="timeLabel" tick={{ fill: '#6b7280', fontSize: 10 }} interval="preserveStartEnd" minTickGap={24} />
                      <YAxis allowDecimals={false} tick={{ fill: '#6b7280', fontSize: 10 }} />
                      <Tooltip formatter={(v) => [`${v} km/h`, 'Speed']} />
                      <Bar dataKey="avgSpeed" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Event radar */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                <FaExclamationTriangle className="inline mr-2 text-gray-400" />
                Driving Event Radar
              </h3>
              <div className="w-full h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} margin={{ top: 6, right: 10, left: 10, bottom: 0 }}>
                    <PolarGrid stroke="#e5e7eb" />
                    <PolarAngleAxis dataKey="event" tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <PolarRadiusAxis allowDecimals={false} tick={{ fill: '#9ca3af', fontSize: 10 }} />
                    <Tooltip />
                    <Radar name="Event profile" dataKey="value" stroke="#6366f1" fill="#818cf8" fillOpacity={0.35} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {trackedRadarEvents.map((item) => (
                  <div key={item.key} className="flex items-center justify-between rounded-md border border-gray-200 px-2 py-1">
                    <span className="flex items-center gap-1 text-[10px] text-gray-700 pr-2">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      {item.label}
                    </span>
                    <span className="text-[10px] font-semibold text-gray-800 shrink-0 min-w-[16px] text-right">
                      {radarCounts[item.key] || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Route map */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={showMap}
                onChange={(e) => setShowMap(e.target.checked)}
                className="w-4 h-4 text-[#00B8B8] border-gray-300 rounded focus:ring-[#00B8B8]"
              />
              <span className="text-sm font-semibold text-gray-700">
                <FaMapMarkerAlt className="inline mr-1 text-gray-400" />
                Show Route Map ({mapPoints.length} points)
              </span>
            </label>

            {showMap && isLoaded && (
              <>
              <div className="w-full h-80 rounded-lg overflow-hidden">
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={{ lat: -1.2921, lng: 36.8219 }}
                  zoom={10}
                  onLoad={(map) => { mapRef.current = map; }}
                  options={{ mapTypeControl: true, streetViewControl: false, fullscreenControl: true, zoomControl: true }}
                >
                  {mapPoints.map((point, idx) => {
                    const isFirst = idx === 0;
                    const isLast = idx === mapPoints.length - 1;

                    if (isFirst) {
                      return (
                        <Marker
                          key={`start-${point.id}`}
                          position={point.position}
                          icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: '#1d4ed8', fillOpacity: 1,
                            strokeColor: '#ffffff', strokeWeight: 2, scale: 8
                          }}
                          title={`Start - ${point.speed != null ? point.speed + ' km/h' : ''} - ${new Date(point.lastUpdated).toLocaleString()}`}
                        />
                      );
                    }

                    if (isLast && mapPoints.length > 1) {
                      return (
                        <Marker
                          key={`end-${point.id}`}
                          position={point.position}
                          icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: '#7c3aed', fillOpacity: 1,
                            strokeColor: '#ffffff', strokeWeight: 2, scale: 8
                          }}
                          title={`End - ${point.speed != null ? point.speed + ' km/h' : ''} - ${new Date(point.lastUpdated).toLocaleString()}`}
                        />
                      );
                    }

                    return (
                      <Marker
                        key={`pt-${point.id}-${idx}`}
                        position={point.position}
                        icon={{
                          path: window.google.maps.SymbolPath.CIRCLE,
                          fillColor: point.isAnomaly ? '#dc2626' : '#16a34a',
                          fillOpacity: 1,
                          strokeColor: '#ffffff', strokeWeight: 1, scale: 5
                        }}
                        title={`${point.eventName || 'Position'}${point.speed != null ? ` - ${point.speed} km/h` : ''}${point.isAnomaly && point.roadSpeed ? ` (limit ${point.roadSpeed} km/h)` : ''} - ${new Date(point.lastUpdated).toLocaleString()}`}
                      />
                    );
                  })}
                </GoogleMap>
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#1d4ed8]" />Start</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#7c3aed]" />End</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#16a34a]" />Normal</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#dc2626]" />Overspeed / Anomaly</span>
              </div>
              </>
            )}

            {showMap && !isLoaded && (
              <div className="w-full h-80 flex items-center justify-center text-sm text-gray-500">Loading map...</div>
            )}
          </div>

          {/* Anomalies list */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={showAnomalies}
                onChange={(e) => setShowAnomalies(e.target.checked)}
                className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-sm font-semibold text-gray-700">
                <FaFlag className="inline mr-1 text-red-500" />
                Show Anomaly Events ({mapPoints.filter((p) => p.isAnomaly).length} flagged)
              </span>
            </label>

            {showAnomalies && (() => {
              const anomalies = mapPoints.filter((p) => p.isAnomaly);
              if (anomalies.length === 0) {
                return (
                  <div className="text-center py-6 text-sm text-gray-500 border border-dashed border-gray-300 rounded-md">
                    No anomaly events in the selected time window
                  </div>
                );
              }
              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider"></th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Event</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Speed</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Limit</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Coordinates</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {anomalies.map((point, idx) => (
                        <tr key={`anomaly-${point.id}-${idx}`} className="hover:bg-red-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <FaFlag className="text-red-500" />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-800 font-medium">
                            {point.eventName || 'Unknown'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-red-600 font-semibold">{point.speed != null ? `${point.speed} km/h` : '-'}</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                            {point.roadSpeed != null && point.roadSpeed > 0 ? `${point.roadSpeed} km/h` : '-'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-500 font-mono">
                            {point.position.lat.toFixed(5)}, {point.position.lng.toFixed(5)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                            {new Date(point.lastUpdated).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          {/* Placeholder sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vehicle Maintenance Logs */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FaWrench className="text-gray-400" />
                Vehicle Maintenance Logs
              </h3>
              <div className="border border-dashed border-gray-300 rounded-md p-6 text-center">
                <FaWrench className="text-3xl text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Coming soon</p>
                <p className="text-xs text-gray-400 mt-1">Maintenance records for vehicles driven by this driver will appear here.</p>
              </div>
            </div>

            {/* Late Trip Starts */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FaClock className="text-gray-400" />
                Late Trip Starts
              </h3>
              <div className="border border-dashed border-gray-300 rounded-md p-6 text-center">
                <FaClock className="text-3xl text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Coming soon</p>
                <p className="text-xs text-gray-400 mt-1">Statistics on trips started late by this driver will appear here.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-lg">{icon}</div>
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-bold text-gray-900">{value}</div>
    </div>
  </div>
);

export default DriverAnalyticsModal;
